// University of Nebraska-Lincoln: public college page (#2744), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UNL's Common Data Set 2025-2026, published as web pages by Institutional
//    Effectiveness and Analytics (iea.unl.edu, one page per section; no PDF):
//    C1 applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C13 to C22 admission policies
//    (no early decision, no early action, rolling notification from
//    August 1), G1/G5 2026-2027 costs.
//  - UNL Undergraduate Admissions: First-Year Dates & Deadlines (the Fall
//    2027 table; quirk: the tab for the 2026 dates is headed "2024 Important
//    Dates and Deadlines", but its rows are the fall 2026 dates for the Fall
//    2027 term) and First-Year admission requirements (assured admission,
//    test policy, college-specific requirements, auditions and portfolios).
//    Neither page publishes decision dates. A search of unl.edu found no
//    announced change for fall 2028 entry.
//  - College Scorecard, school id 181464 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the CDS G1/G5 items. G5 leaves transportation blank, so there is
// no transportation row, and the totals are the sum of the items shown.
// G4 says tuition and fees vary by program (43 percent of full-time
// undergraduates pay more than the G1 figures).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UNL Common Data Set 2025-2026',
        url: 'https://iea.unl.edu/common-data-set-2025-2026/',
        read: '2026-10-01',
    },
    dates: {
        label: 'UNL Admissions, First-Year Dates and Deadlines',
        url: 'https://admissions.unl.edu/apply/first-year-dates-deadlines/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'UNL Admissions, First-Year Admission Requirements',
        url: 'https://admissions.unl.edu/apply/admission-requirements/first-year/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?181464-University-of-Nebraska-Lincoln',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-nebraska-lincoln',
    kbId: 'university_of_nebraska_lincoln',
    name: 'University of Nebraska-Lincoln',
    shortName: 'Nebraska',
    aliases: ['UNL'],
    location: 'Lincoln, NE',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UNL brand colors, ucomm.unl.edu); decoration only.
    branding: {
        primary: '#D00000',
        secondary: '#F5F1E7',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_nebraska_lincoln_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 18648,
        admitted: 16372,
        enrolled: 4734,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 9229, admitted: 8169 },
            { group: 'Out-of-state', applied: 8413, admitted: 7673 },
            { group: 'International', applied: 1006, admitted: 530 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UNL\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UNL has no early decision or early action plan; its Common Data Set says admission decisions go out on a rolling basis starting August 1. The November 1 priority deadline sets when you hear about scholarships, not an early decision.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You are guaranteed admission to UNL if you meet its core course requirements and one performance requirement. Some colleges and programs ask for more; if you do not meet a college\'s added requirements, you start in the Explore Center and move into that college once you do.',
        rows: [
            { school: 'College of Architecture', requirement: 'Four units of math through pre-calculus or trigonometry (or calculus), an ACT composite of 22 or SAT of 1110, and either the upper 25 percent of your class or a 3.2 GPA.' },
            { school: 'College of Engineering', requirement: 'Four units of math through pre-calculus or trigonometry (or calculus) and three units of science (chemistry, physics, and biology or earth science), plus one of: ACT composite 24, SAT 1180, ACT math 24, SAT math 580, or a 3.5 GPA.' },
            { school: 'Music and dance', requirement: 'Auditions are required.' },
            { school: 'Acting', requirement: 'Auditions are required, through the Johnny Carson School of Theatre and Film.' },
            { school: 'Entertainment Design and Production, Emerging Media Arts', requirement: 'An application and a portfolio are required.' },
            { school: 'PGA Golf Management', requirement: 'A 12 handicap is required to enter the program.' },
            { school: 'Raikes School of Computer Science and Management, Nebraska Business Honors Academy', requirement: 'Separate applications, due November 15, 2026.', source: 'dates' },
        ],
        note: 'Art and art history take no audition or special application; a portfolio review is optional and only for added scholarships.',
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'May 1' },
        rows: [
            { plan: 'Priority deadline', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Final deadline', deadline: 'May 1', decision: null, binding: false },
        ],
        note: 'The application opens August 1, 2026. If you apply by November 1, scholarship offers come February 11, 2027. To be considered for academic scholarships, in-state students must send all application materials by February 1 and out-of-state students by May 1. The enrollment deposit is also due May 1. Classes begin August 23, 2027. UNL does not publish decision dates.',
        source: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UNL does not require the ACT or SAT for admission. It encourages every first-year applicant to send an official score for full scholarship consideration, and awards the better of a scholarship based on your GPA alone or on your GPA with your highest composite score. An ACT of 20 or an SAT of 1040 is one of four ways to meet the performance requirement for guaranteed admission; the others are a 3.0 GPA or a rank in the top half of your class.',
        source: 'requirements',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by July 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'requirements',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [9090, 29160] },
            { item: 'Required fees', amounts: [2382, 2382] },
            { item: 'Housing (on campus)', amounts: [7960, 7960] },
            { item: 'Food (on-campus meal plan)', amounts: [5990, 5990] },
            { item: 'Books and supplies', amounts: [1128, 1128] },
            { item: 'Other expenses', amounts: [2388, 2388] },
        ],
        totals: [28938, 49008],
        note: 'For a full-time undergraduate living on campus, fall and spring. UNL\'s Common Data Set gives no transportation figure, so none is included. Tuition and fees vary by program: UNL says 43 percent of full-time undergraduates pay more than these figures.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 16214,
            byIncome: [
                { income: '$0 to $30,000', amount: 11449 },
                { income: '$30,001 to $48,000', amount: 11572 },
                { income: '$48,001 to $75,000', amount: 12551 },
                { income: '$75,001 to $110,000', amount: 17140 },
                { income: 'Over $110,000', amount: 21693 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UNL\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
